// ---------------------------------------------------------------------------
// UITLProvider — React context provider for the UITL Admin SDK
//
// Creates a React context that holds the API client, remote SDK config,
// user identity, and theme. All other SDK components consume this context.
//
// Usage:
//   const config: UITLConfig = { apiKey: 'pk_...', user: { id: 'u1' } };
//   <UITLProvider config={config}>
//     <ChatWidget />
//     <FeedbackButton />
//   </UITLProvider>
// ---------------------------------------------------------------------------

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { UITLConfig, SDKConfigResponse } from '../types';
import { UITLApiClient } from '../api';

// ---------------------------------------------------------------------------
// Resolved theme — all values guaranteed present
// ---------------------------------------------------------------------------

export interface UITLResolvedTheme {
  primaryColor: string;
  primaryHover: string;
  backgroundColor: string;
  surfaceColor: string;
  textColor: string;
  textSecondary: string;
  borderColor: string;
  successColor: string;
  warningColor: string;
  errorColor: string;
  fontFamily: string;
  borderRadius: number;
  zIndex: number;
}

const DEFAULT_THEME: UITLResolvedTheme = {
  primaryColor: '#2563EB',
  primaryHover: '#1D4ED8',
  backgroundColor: '#FFFFFF',
  surfaceColor: '#F8FAFC',
  textColor: '#1E293B',
  textSecondary: '#64748B',
  borderColor: '#E2E8F0',
  successColor: '#16A34A',
  warningColor: '#D97706',
  errorColor: '#DC2626',
  fontFamily:
    "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  borderRadius: 8,
  zIndex: 99990,
};

// ---------------------------------------------------------------------------
// Context value
// ---------------------------------------------------------------------------

export interface UITLContextValue {
  /** The API client instance for making SDK requests. */
  client: UITLApiClient;
  /** The original config object passed to the provider. */
  config: UITLConfig;
  /** Remote config fetched from /api/v1/sdk/config on mount. */
  remoteConfig: SDKConfigResponse | null;
  /** Resolved theme with defaults filled in. */
  theme: UITLResolvedTheme;
  /** True once the remote config has been fetched (or errored). */
  ready: boolean;
}

const UITLContext = createContext<UITLContextValue | null>(null);

/**
 * Access the UITL SDK context from any child component.
 * Throws if called outside a <UITLProvider>.
 */
export function useUITL(): UITLContextValue {
  const ctx = useContext(UITLContext);
  if (!ctx) {
    throw new Error(
      '[UITL SDK] useUITL() must be used inside a <UITLProvider>',
    );
  }
  return ctx;
}

// ---------------------------------------------------------------------------
// Provider component
// ---------------------------------------------------------------------------

export interface UITLProviderProps {
  config: UITLConfig;
  children: React.ReactNode;
}

/**
 * Wraps your application (or a subtree) to provide UITL SDK context.
 *
 * On mount it:
 * 1. Creates a UITLApiClient from the config
 * 2. Fetches the remote SDK config from the backend
 * 3. Merges the theme with defaults
 * 4. Fires config.onReady() when complete
 */
export function UITLProvider({ config, children }: UITLProviderProps) {
  const client = useMemo(() => new UITLApiClient(config), [config]);

  const [remoteConfig, setRemoteConfig] = useState<SDKConfigResponse | null>(
    null,
  );
  const [ready, setReady] = useState(false);

  // Merge user-supplied theme overrides with defaults
  const theme = useMemo<UITLResolvedTheme>(() => {
    const overrides = config.theme ?? {};
    return {
      ...DEFAULT_THEME,
      ...(overrides.primaryColor ? { primaryColor: overrides.primaryColor } : {}),
      ...(overrides.fontFamily ? { fontFamily: overrides.fontFamily } : {}),
      ...(overrides.borderRadius !== undefined
        ? { borderRadius: overrides.borderRadius }
        : {}),
    };
  }, [config.theme]);

  // Fetch remote config on mount
  useEffect(() => {
    let cancelled = false;

    client
      .getConfig()
      .then((rc) => {
        if (!cancelled) {
          setRemoteConfig(rc);
          setReady(true);
          config.onReady?.();
        }
      })
      .catch((err) => {
        console.error('[UITL SDK] Failed to fetch remote config:', err);
        if (!cancelled) {
          setReady(true);
          // Still fire onReady so the caller is not left waiting
          config.onReady?.();
        }
      });

    return () => {
      cancelled = true;
    };
  }, [client, config]);

  const value = useMemo<UITLContextValue>(
    () => ({ client, config, remoteConfig, theme, ready }),
    [client, config, remoteConfig, theme, ready],
  );

  return (
    <UITLContext.Provider value={value}>{children}</UITLContext.Provider>
  );
}
